import { Injectable } from '@angular/core';
import { createStore, select, setProps, withProps } from '@ngneat/elf';
import {
  deleteEntities,
  getActiveEntity,
  getActiveId,
  getEntity,
  hasEntity,
  selectActiveEntity,
  selectActiveId,
  selectAllEntities,
  setActiveId,
  setEntities,
  updateEntities,
  upsertEntities,
  withActiveId,
  withEntities,
} from '@ngneat/elf-entities';
import { entitiesStateHistory } from '@ngneat/elf-state-history';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, tap } from 'rxjs';
import { Form, ReadonlyState } from '../models/form.model';
import { Question } from '../models/question/question.model';
import { Section } from '../models/section.model';
import {
  SectionQuestion,
  SectionStateService,
} from '../services/section-state.service';
import { write } from './mutations';

const changesAnswersTypes = [
  'budget',
  'worksite',
  'date-change-request',
] as const;
export type ChangesAnswerType = (typeof changesAnswersTypes)[number];

const store = createStore(
  { name: 'renderer-forms' },
  withEntities<Form>(),
  withProps<{
    isLoading: boolean;
    activeUploadCount: number;
    readonly: ReadonlyState;
    hideSections: string[];
    isSharedView: boolean;
    showChanges: boolean;
    showYearly: boolean;
    isChangeRequest: boolean;
    isApplicationCompletion: boolean;
    hasChangeRequestInDb: boolean;
    showAnswers: boolean;
    isTemplate: boolean;
  }>({
    isLoading: false,
    activeUploadCount: 0,
    readonly: ReadonlyState.Editable,
    hideSections: [],
    isSharedView: false,
    showChanges: false,
    showYearly: false,
    isChangeRequest: false,
    isApplicationCompletion: false,
    hasChangeRequestInDb: undefined,
    showAnswers: false,
    isTemplate: false,
  }),
  withActiveId(),
);

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class RendererFormsRepository {
  forms$ = store.pipe(selectAllEntities());
  active$ = store.pipe(selectActiveEntity());
  activeId$ = store.pipe(selectActiveId());
  isLoading$ = store.pipe(select((state) => state.isLoading));
  activeUploadCount$ = store.pipe(select((state) => state.activeUploadCount));
  hasActiveUploads$ = store.pipe(
    select((state) => state.activeUploadCount > 0),
  );
  readonly$ = store.pipe(select((state) => state.readonly));
  showChanges$ = store.pipe(select((state) => state.showChanges));
  showYearly$ = store.pipe(select((state) => state.showYearly));
  isSharedView$ = store.pipe(select((state) => state.isSharedView));
  showAnswers$ = store.pipe(select((state) => state.showAnswers));
  isTemplate$ = store.pipe(select((state) => state.isTemplate));

  showChangesAnswers: Map<string, boolean> = new Map<string, boolean>();

  constructor(private sectionService: SectionStateService) {
    this.updateSectionService();
  }

  has(formId: string) {
    return store.query(hasEntity(formId));
  }

  getActive() {
    return store.query(getActiveEntity());
  }

  getActiveId() {
    return store.query(getActiveId);
  }

  get(formId: string) {
    return store.query(getEntity(formId));
  }

  getReadonly() {
    return store.state.readonly;
  }

  getIsChangeRequest() {
    return store.state.isChangeRequest;
  }

  getIsApplicationCompletion() {
    return store.state.isApplicationCompletion;
  }

  getChangeRequestExistsInDb() {
    return store.state.hasChangeRequestInDb;
  }

  getHideSections() {
    return store.state.hideSections;
  }

  getIsSharedView() {
    return store.state.isSharedView;
  }

  getShowChanges() {
    return store.state.showChanges;
  }
  getShowYearly() {
    return store.state.showYearly;
  }
  getIsTemplate() {
    return store.state.isTemplate;
  }

  set(forms: Form[]) {
    store.update(setEntities(forms));
  }

  upsert(form: Form) {
    store.update(upsertEntities(form));
  }

  update(formId: string, updater: (state: Form) => void) {
    store.update(updateEntities(formId, write<Form>(updater)));
  }

  updateActive(updater: (state: Form) => void) {
    store.update(updateEntities(this.getActiveId(), write<Form>(updater)));
  }

  setActive(formId: string) {
    store.update(setActiveId(formId));
  }

  setLoading(isLoading: boolean) {
    store.update(setProps(write((state) => (state.isLoading = isLoading))));
  }

  getLoading() {
    return store.state.isLoading;
  }

  incrementUploadCount() {
    store.update(setProps(write((state) => (state.activeUploadCount += 1))));
  }

  decrementUploadCount() {
    store.update(
      setProps(
        write((state) => {
          state.activeUploadCount = Math.max(0, state.activeUploadCount - 1);
        }),
      ),
    );
  }

  setReadonly(readonlyState: ReadonlyState) {
    store.update(setProps(write((state) => (state.readonly = readonlyState))));
  }

  setIsSharedView(isSharedView: boolean) {
    store.update(
      setProps(write((state) => (state.isSharedView = isSharedView))),
    );
  }

  setShowChanges(showChanges: boolean) {
    store.update(setProps(write((state) => (state.showChanges = showChanges))));
  }
  setShowYearly(showYearly: boolean) {
    store.update(setProps(write((state) => (state.showYearly = showYearly))));
  }

  setShowChangesAnswers(answertype: ChangesAnswerType, hasAnswer: boolean) {
    this.showChangesAnswers.set(answertype, hasAnswer);
    if (hasAnswer) {
      store.update(setProps(write((state) => (state.showAnswers = true))));
    } else {
      const otherAnswerTypes = changesAnswersTypes.filter(
        (type) => type !== answertype,
      );
      store.update(
        setProps(
          write(
            (state) =>
              (state.showAnswers = otherAnswerTypes.some((type) =>
                this.showChangesAnswers.get(type),
              )),
          ),
        ),
      );
    }
  }

  setChangeRequestExistsInDb(hasChangeRequest: boolean) {
    store.update(
      setProps(
        write((state) => (state.hasChangeRequestInDb = hasChangeRequest)),
      ),
    );
  }

  setIsChangeRequest(isChangeRequest: boolean) {
    store.update(
      setProps(write((state) => (state.isChangeRequest = isChangeRequest))),
    );
  }

  setIsApplicationCompletion(isApplicationCompletion: boolean) {
    store.update(
      setProps(
        write(
          (state) => (state.isApplicationCompletion = isApplicationCompletion),
        ),
      ),
    );
  }

  setIsTemplate(isTemplate: boolean) {
    store.update(setProps(write((state) => (state.isTemplate = isTemplate))));
  }

  setHideSections(hideSections: string[]) {
    store.update(
      setProps(write((state) => (state.hideSections = hideSections))),
    );
  }

  reset() {
    store.reset();
  }

  remove(formId: string) {
    store.update(deleteEntities(formId));
  }

  getHistoryCollection() {
    return entitiesStateHistory(store);
  }

  private updateSectionService(): void {
    this.active$
      .pipe(
        untilDestroyed(this),
        filter((form): form is Form => !!form),
        tap((form) => {
          for (const section of form.sections) {
            const _section: Section = section;
            const _questions = _section.questions;
            this.sectionService.initSectionState(
              _section.id,
              _questions.map(
                (q: Question): SectionQuestion => ({ id: q.id, type: q.type }),
              ),
            );
          }
        }),
      )
      .subscribe();
  }
}
